/**
 * Helpers that translate the chatbot's structured directives into UI actions.
 *
 * `set_filters` events arrive as { page, filters }. Filters in this codebase
 * live in component-local state synchronised to URL search params, so the
 * dispatch is simple: build `/<page>?key=value&...` and navigate.
 *
 * Path mapping uses the same vocabulary the model is told about in the
 * `enrich_response` schema, so the model emits names that translate 1:1.
 */
import type { FiltersDirective } from './innoq';

const PAGE_TO_PATH: Record<string, string> = {
  overview:   '/',
  members:    '/members',
  debates:    '/debates',
  bills:      '/bills',
  votes:      '/votes',
  parties:    '/parties',
  sessions:   '/sessions',
  committees: '/committees',
};

/**
 * Reverse map for dashboard_state — convert the current URL pathname back to
 * the canonical page name the model knows.
 */
export function pathToPageName(pathname: string): string {
  // List pages: exact match.
  for (const [page, path] of Object.entries(PAGE_TO_PATH)) {
    if (pathname === path) return page;
  }
  // Detail pages and unknown routes: take the first path segment, fall back
  // to 'overview' for the root.
  const firstSegment = pathname.split('/').filter(Boolean)[0];
  if (firstSegment && Object.values(PAGE_TO_PATH).includes(`/${firstSegment}`)) {
    return firstSegment;
  }
  return 'overview';
}

/**
 * Build a navigable URL (path + querystring) from a filters directive.
 * Returns null if the page name is unknown — caller can ignore silently.
 */
/** URL flag set by chatbot-driven navigations. Pages read this to know which
 * filter values were applied by the chat (vs. set by the user). When the user
 * changes any filter, the page strips this flag. */
export const CHAT_PROVENANCE_PARAM = '_chat';

export function buildDirectiveUrl(directive: FiltersDirective): string | null {
  const path = PAGE_TO_PATH[directive.page];
  if (!path) return null;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(directive.filters ?? {})) {
    if (value === null || value === undefined || value === '') continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      params.set(key, value.map(v => String(v)).join(','));
    } else if (typeof value === 'object') {
      // Nested objects — JSON-encode. Pages reading this should JSON.parse,
      // but most filter values are scalars so this branch is rare.
      params.set(key, JSON.stringify(value));
    } else {
      params.set(key, String(value));
    }
  }
  // Mark this navigation as chat-driven so the page can render a 🤖 icon
  // beside each filter chip until the user touches it.
  params.set(CHAT_PROVENANCE_PARAM, '1');

  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}
